import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Link
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/EditSharp';
import { compareDateObjects } from '~/modules/common/dates/compare';
import NoValue from '~/modules/common/components/NoValue';
import DelaRecommendationButtons from '~/modules/common/components/DelaRecommendationButtons';
import { DelaStarIcon } from '~/modules/common/components/Icons';
import {
  mapRepliconDateToUtcObject,
  mapJSDateToRepliconDate
} from '~/modules/common/dates/convert';
import { useMeContext } from '~/modules/me/useMeContext';

const useStyles = makeStyles(theme => ({
  certificateCard: {
    boxShadow: '0 0 0 0'
  },
  contentRoot: {
    display: 'flex',
    padding: 0,
    columnGap: theme.spacing(0.5)
  },
  certificateContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    justifyContent: 'center'
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  headerRoot: {
    padding: 0
  },
  headerContent: {
    overflow: 'hidden'
  },
  editIcon: {
    margin: 0
  }
}));

const useDelaRecommendationButtonsStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0.25, 0.5, 0.25, 0)
  }
}));

const useDelaStarIconStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(0, 0.5),
    fill: theme.palette.Dela.darkIcon,
    fontSize: theme.spacing(1.8)
  }
}));

export const CertificateReadOnly = ({
  editable,
  certificate,
  canEditSkills,
  onEdit,
  onAcceptCertificateNudges,
  onRejectCertificateNudges
}) => {
  const {
    featureFlags: { isPsaPraaUserProfileNudgesEnabled }
  } = useMeContext();
  const intl = useIntl();
  const classes = useStyles();
  const delaRecommendationButtonsClasses = useDelaRecommendationButtonsStyles();
  const delaStarIconClasses = useDelaStarIconStyles();

  const {
    name,
    issuer,
    issueDate,
    expiryDate,
    credentialId,
    credentialUrl,
    expires,
    isDelaRecommendation
  } = certificate;

  const today = new Date();
  const linkDataQeId = `${name}_Link`;
  const editButtonQeId = `${name}_Edit`;
  const credentialDescriptionQeId = `${name}_Credentials`;
  const descriptionQeId = `${name}_Description`;

  const expiry = expiryDate
    ? {
        expiryDate: mapRepliconDateToUtcObject(expiryDate).toLocaleString({
          month: 'short',
          year: 'numeric'
        })
      }
    : null;

  const issuedOnDate = issueDate
    ? {
        issueDate: mapRepliconDateToUtcObject(issueDate).toLocaleString({
          month: 'short',
          year: 'numeric'
        })
      }
    : null;

  const isCertificateExpired =
    expiryDate &&
    compareDateObjects(mapJSDateToRepliconDate(today), expiryDate) === 1;

  const isAnyDescriptionValuePresent =
    issueDate || expiryDate || issuer || !expires;
  const isAllDescriptionValuesPresent = issueDate && expiryDate && issuer;

  const issuedOn = issuedOnDate ? (
    <span>
      <FormattedMessage
        id="resourceCertificatesAndLicensesCard.issuedByOn"
        values={issuedOnDate}
      />
    </span>
  ) : null;

  const expiresOn =
    expires && expiry ? (
      <span>
        <FormattedMessage
          id="resourceCertificatesAndLicensesCard.expiresOn"
          values={expiry}
        />
      </span>
    ) : null;

  const doesNotExpire = (
    <span>
      <FormattedMessage id="resourceCertificatesAndLicensesCard.doesntExpire" />
    </span>
  );

  const issuedBy = issuer || <NoValue />;

  const descriptionValues = useMemo(
    () => ({
      issuedOn,
      expiresOn: expires ? expiresOn : doesNotExpire,
      issuer: issuedBy
    }),
    [expires, issuedBy, issuedOn, expiresOn, doesNotExpire]
  );

  const issuedByComponent = issuer ? (
    <FormattedMessage
      id="resourceCertificatesAndLicensesCard.issuedBy"
      values={{ issuer }}
    />
  ) : null;

  const issuedOnComponent = issueDate ? (
    <FormattedMessage
      id={
        issuer
          ? 'resourceCertificatesAndLicensesCard.issuedByOn'
          : 'resourceCertificatesAndLicensesCard.issuedOn'
      }
      values={issuedOnDate}
    />
  ) : null;

  const expiredComponent = (
    <FormattedMessage
      id={
        issuer || issueDate
          ? 'resourceCertificatesAndLicensesCard.issuedByOrOnWithExpired'
          : 'resourceCertificatesAndLicensesCard.expired'
      }
      values={expiry}
    />
  );

  const expiresOnComponent = (
    <FormattedMessage
      id={
        issuer || issueDate
          ? 'resourceCertificatesAndLicensesCard.issuedByOrOnWithExpiresOn'
          : 'resourceCertificatesAndLicensesCard.expiresOn'
      }
      values={expiry}
    />
  );

  const expiryComponent = expiresOn
    ? isCertificateExpired
      ? expiredComponent
      : expiresOnComponent
    : null;

  const credentialIdDescriptionValues = useMemo(
    () => ({
      credentialId,
      credentialUrl: credentialUrl && (
        <Link
          data-qe-id={linkDataQeId}
          href={credentialUrl}
          target="_blank"
          color="default"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="resourceCertificatesAndLicensesCard.seeCredential" />
        </Link>
      )
    }),
    [credentialId, credentialUrl, linkDataQeId]
  );

  const cardHeaderClasses = useMemo(
    () => ({
      title: classes.title,
      root: classes.headerRoot,
      action: classes.editIcon,
      content: classes.headerContent
    }),
    [classes.editIcon, classes.headerContent, classes.headerRoot, classes.title]
  );

  const cardContentClasses = useMemo(
    () => ({
      root: isPsaPraaUserProfileNudgesEnabled
        ? classes.contentRoot
        : classes.certificateContent
    }),
    [
      classes.contentRoot,
      classes.certificateContent,
      isPsaPraaUserProfileNudgesEnabled
    ]
  );

  const onAccept = useCallback(() => {
    onAcceptCertificateNudges([certificate]);
  }, [onAcceptCertificateNudges, certificate]);

  const onReject = useCallback(() => {
    onRejectCertificateNudges([certificate]);
  }, [onRejectCertificateNudges, certificate]);

  const showDelaRecommendationButtons =
    canEditSkills && isPsaPraaUserProfileNudgesEnabled && isDelaRecommendation;

  return (
    <Card className={classes.certificateCard}>
      <CardHeader
        classes={cardHeaderClasses}
        title={name}
        action={
          editable &&
          (!isPsaPraaUserProfileNudgesEnabled || !isDelaRecommendation) && (
            <IconButton
              aria-label={name}
              data-qe-id={editButtonQeId}
              onClick={onEdit}
            >
              <EditIcon />
            </IconButton>
          )
        }
      />
      <CardContent data-qe-id={descriptionQeId} classes={cardContentClasses}>
        {showDelaRecommendationButtons && (
          <DelaRecommendationButtons
            classes={delaRecommendationButtonsClasses}
            onAccept={onAccept}
            onReject={onReject}
          />
        )}
        <div className={classes.certificateContent}>
          {isAllDescriptionValuesPresent ? (
            <span>
              <FormattedMessage
                id={
                  isCertificateExpired
                    ? 'resourceCertificatesAndLicensesCard.alreadyExpiredDescription'
                    : 'resourceCertificatesAndLicensesCard.description'
                }
                values={descriptionValues}
              />
              {isPsaPraaUserProfileNudgesEnabled && isDelaRecommendation && (
                <DelaStarIcon
                  classes={delaStarIconClasses}
                  titleAccess={intl.formatMessage({
                    id: 'dela.suggestedByDelaAI'
                  })}
                />
              )}
            </span>
          ) : (
            <span>
              {isAnyDescriptionValuePresent ? (
                <>
                  {issuedByComponent}
                  {issuedOnComponent}
                  {expires ? (
                    expiryComponent
                  ) : (
                    <FormattedMessage
                      id={
                        issuer || issueDate
                          ? 'resourceCertificatesAndLicensesCard.issuedByOrOnWithDoesNotExpire'
                          : 'resourceCertificatesAndLicensesCard.doesntExpire'
                      }
                    />
                  )}
                </>
              ) : null}
              {isPsaPraaUserProfileNudgesEnabled && isDelaRecommendation && (
                <DelaStarIcon
                  classes={delaStarIconClasses}
                  titleAccess={intl.formatMessage({
                    id: 'dela.suggestedByDelaAI'
                  })}
                />
              )}
            </span>
          )}
          {credentialId && (
            <div data-qe-id={credentialDescriptionQeId}>
              <span>
                <FormattedMessage
                  id="resourceCertificatesAndLicensesCard.credentialIdDescription"
                  values={credentialIdDescriptionValues}
                />
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

CertificateReadOnly.propTypes = {
  editable: PropTypes.bool.isRequired,
  certificate: PropTypes.object.isRequired,
  canEditSkills: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAcceptCertificateNudges: PropTypes.func.isRequired,
  onRejectCertificateNudges: PropTypes.func.isRequired
};

export default CertificateReadOnly;
